<template>
  <div>
    <div v-if="commitPointer" class="mb-4 grey--text text--darken-2">{{ commitPointer }}</div>

    <div class="mb-4">
      <div class="font-weight-medium mb-1">Kick</div>
      <div>
        {{ kickText }}
        <span v-if="record && record.kickedAt">. kicked {{ when(record.kickedAt) }}</span>
      </div>
      <step-button
        name="canKick"
        label="Kick all..."
        :steps="preflight.steps"
        :busy="busy"
        :loading="loadingKey === 'kick'"
        @click="openKick"
      />
      <div class="caption grey--text text--darken-1">
        Running Kick all again drops every session once more.
      </div>
      <kick-run-view :run="kickRun" :loop="kickLoop" :busy="kickBusy" :note="kickNote" :poi-state="preflight.poiState" />
      <v-alert v-if="guidance.show" type="warning" dense outlined class="mt-2 mb-0">
        <div class="font-weight-medium">{{ guidance.title }}</div>
        <div class="mt-1">{{ guidance.body }}</div>
        <v-btn small outlined color="primary" class="mt-2 mr-2" @click="downloadGuidance">Download list ({{ guidance.count }})</v-btn>
        <step-button
          v-if="retryIds.length"
          name="canKick"
          :label="`Retry failed and not sent (${retryIds.length})`"
          :steps="preflight.steps"
          color="secondary"
          :busy="busy"
          :loading="loadingKey === 'kick-retry'"
          @click="retryKick"
        />
      </v-alert>
    </div>

    <div class="mb-4">
      <div class="font-weight-medium mb-1">Reconnection</div>
      <div v-if="reconnect">
        {{ summary.text }}
        <v-btn x-small text color="primary" :disabled="busy || checkState.disabled" :title="checkState.reason" @click="refreshReconnect">Refresh</v-btn>
        <span v-if="checkState.disabled" class="caption grey--text text--darken-1">{{ checkState.reason }}</span>
        <v-checkbox
          :input-value="autoOn"
          label="auto 60 s"
          dense
          hide-details
          class="d-inline-block mt-0 pt-0 ml-2"
          @change="$emit('auto', $event)"
        />
      </div>
      <div v-else class="grey--text text--darken-1">
        Not checked yet.
        <v-btn x-small text color="primary" :disabled="busy || checkState.disabled" :title="checkState.reason" @click="refreshReconnect">Refresh</v-btn>
        <span v-if="checkState.disabled" class="caption grey--text text--darken-1">{{ checkState.reason }}</span>
      </div>
      <div v-if="reconnectError" class="orange--text text--darken-3">Reconnection check failed: {{ reconnectError }}</div>
      <div v-if="referenceText" class="caption grey--text text--darken-1">{{ referenceText }}</div>
      <v-simple-table v-if="table.length" dense :height="table.length > 8 ? 320 : undefined" fixed-header class="mt-2">
        <thead>
          <tr>
            <th>AVC</th><th>User</th><th>IP type</th><th>Since kick</th><th>Last session</th><th>NAS</th><th>IP</th><th>Status</th><th>Advice</th><th />
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in table" :key="`rc-${r.avcId}`">
            <td>{{ r.avcId }}</td>
            <td>{{ r.user }}</td>
            <td>{{ r.ipType }}</td>
            <td>{{ r.minutes === null ? "-" : `${r.minutes} min` }}</td>
            <td>{{ r.lastSession }}</td>
            <td>{{ r.nas }}</td>
            <td>{{ r.ip }}</td>
            <td :class="adviceClass(r.statusTone)">
              {{ r.status }}
              <div v-if="r.session" class="caption grey--text text--darken-1">{{ r.session }}</div>
            </td>
            <td :class="adviceClass(r.adviceTone)">
              {{ r.advice }}
              <div v-if="r.proof" :class="`caption ${adviceClass(r.proof.tone)}`">{{ r.proof.text }}</div>
            </td>
            <td class="text-no-wrap">
              <v-btn x-small text color="primary" :disabled="busy || actions(r).kick.disabled" :title="actions(r).kick.reason" @click="askKickOne(r)">Kick this</v-btn>
              <v-btn x-small text color="primary" :disabled="busy || actions(r).verify.disabled" :title="actions(r).verify.reason" @click="verifyOne(r)">Verify this AVC</v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
      <v-btn v-if="table.length" small outlined color="primary" class="mt-2" :disabled="!offlineCount" @click="downloadOffline">
        Download offline list ({{ offlineCount }})
      </v-btn>
      <div v-if="verifying && verifyProgress" class="mt-2">
        <v-progress-linear :value="((verifyProgress.index - 1) / verifyProgress.total) * 100" height="18" />
        <div class="mt-1">{{ verifyProgressText }}</div>
      </div>
    </div>

    <div class="mb-2">
      <div class="font-weight-medium mb-1">Mark migrated</div>
      <step-button
        name="canMarkMigrated"
        label="Mark migrated..."
        :steps="preflight.steps"
        :busy="busy"
        :loading="loadingKey === 'migrated'"
        @click="askMarkMigrated"
      />
      <div v-if="record && record.completedAt" class="green--text text--darken-2">
        Marked migrated {{ when(record.completedAt) }} by {{ record.completedBy }}
      </div>
    </div>

    <v-alert v-if="alert" :type="alert.type" dense outlined class="mt-3 mb-0">
      {{ alert.text }}
      <v-btn v-if="alert.type === 'error'" small text class="ml-2" @click="retryLast">Retry</v-btn>
    </v-alert>

    <confirm-action
      :dialog="confirm.open"
      :text="confirm.text"
      :proceed-text="confirm.proceedText"
      cancel-text="Cancel"
      @onBtnClick="onConfirm"
    />
    <v-dialog v-model="kickOpen" persistent max-width="480">
      <v-card>
        <v-card-title class="primary"><span class="white--text">Kick all</span></v-card-title>
        <v-card-text class="pt-4 black--text">
          <p class="mb-2">{{ kickDlg.text }}</p>
          <p v-if="kickDlg.lastLine" class="mb-2">{{ kickDlg.lastLine }}</p>
          <v-checkbox
            v-if="kickDlg.checkboxLabel"
            v-model="includeUncommitted"
            :label="kickDlg.checkboxLabel"
            dense
            hide-details
          />
          <v-checkbox
            v-if="kickDlg.onlineCheckboxLabel"
            v-model="includeOnline"
            :label="kickDlg.onlineCheckboxLabel"
            dense
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="kickOpen = false">Cancel</v-btn>
          <v-btn color="error" :disabled="!kickDlg.ids.length" @click="kickAll">{{ kickDlg.proceedText }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { downloadText } from "@/utils/csv";
import { formatDual } from "@/utils/zonedTime";
import {
  commitPointerLine,
  kickDialog,
  kickGuidance,
  kickLine,
  kickOneText,
  kickPlan,
  kickThisAlert,
  markMigratedText,
  reconnectActions,
  reconnectRowsOf,
  referenceLabel,
  reconnectSummary,
  reconnectTable,
  stepButton,
  toKickGuidanceCsv,
  toOfflineCsv,
} from "@/utils/cvcMigrationPage";
import StepButton from "./step-button";
import KickRunView from "./kick-run-view";
import SectionActions from "./section-actions";
import VerifyRun from "./verify-run";

const ADVICE_CLASS = {
  green: "green--text text--darken-2",
  amber: "orange--text text--darken-3",
  red: "red--text text--darken-2",
  grey: "grey--text text--darken-2",
};

// Section D: write the new static IPs, drop the sessions so they redial through
// the new router, watch them come back, then close the migration.
export default {
  name: "CvcMigrationAfter",
  components: { StepButton, KickRunView },
  mixins: [SectionActions, VerifyRun],
  // The page view owns the kick run (it outlives this panel); this section asks
  // it to start one and renders what it holds.
  inject: ["cvcKickStart"],
  props: {
    preflight: { type: Object, required: true },
    cvcId: { type: Number, required: true },
    targetHostId: { type: Number, required: true },
    now: { type: Date, required: true },
    reconnect: { type: Object, default: null },
    reconnectError: { type: String, default: "" },
    // The "auto 60 s" switch of the reconnection check; the page owns it.
    autoOn: { type: Boolean, default: true },
    // The latest kick run of the CVC, whether this tab runs a loop for it, the
    // note to show under Kick, the latest kick per service and the last Kick all.
    kickRun: { type: Object, default: null },
    kickLoop: { type: Boolean, default: false },
    kickBusy: { type: Boolean, default: false },
    kickNote: { type: String, default: "" },
    kickRowMap: { type: Object, default: () => ({}) },
    kickRetryIds: { type: Array, default: () => [] },
    lastKickRun: { type: Object, default: null },
  },
  data: () => ({
    kickOpen: false,
    includeUncommitted: false,
    includeOnline: false,
  }),
  computed: {
    record () {
      return this.preflight.record;
    },
    rows () {
      return this.preflight.rows || [];
    },
    target () {
      return this.preflight.targetHostname;
    },
    commitPointer () {
      return commitPointerLine(this.rows);
    },
    kickPlan () {
      return kickPlan(this.rows, reconnectRowsOf(this.reconnect));
    },
    kickText () {
      return kickLine(this.kickPlan);
    },
    kickDlg () {
      return kickDialog(this.rows, this.target, this.includeUncommitted, {
        lastRun: this.lastKickRun,
        poiState: this.preflight.poiState,
        reconnectRows: reconnectRowsOf(this.reconnect),
        includeOnline: this.includeOnline,
      });
    },
    guidance () {
      return kickGuidance({
        kickRows: this.kickRowMap,
        reconnect: this.reconnect,
        rows: this.rows,
        wholesalerNames: this.preflight.wholesalerNames,
        nowUtc: this.now,
      });
    },
    retryIds () {
      return this.kickRetryIds.length ? this.kickRetryIds : this.guidance.retryIds;
    },
    summary () {
      return reconnectSummary(this.reconnect);
    },
    table () {
      return reconnectTable({
        reconnect: this.reconnect,
        rows: this.rows,
        wholesalerNames: this.preflight.wholesalerNames,
        kickedAt: this.record ? this.record.kickedAt : null,
        nowUtc: this.now,
        poiState: this.preflight.poiState,
        record: this.record,
        kickByService: this.kickRowMap,
      });
    },
    referenceText () {
      return referenceLabel(this.reconnect, this.preflight.poiState);
    },
    offlineCount () {
      return this.table.filter(r => r.offline).length;
    },
    checkState () {
      return stepButton(this.preflight.steps, "canVerifyReconnected");
    },
  },
  methods: {
    ...mapActions("cvcMigration", [
      "cvcMigrationVerifyReconnected",
      "cvcMigrationMarkMigrated",
    ]),
    when (utc) {
      return formatDual(utc, this.preflight.poiState);
    },
    // The same reasons as the grid row menu.
    actions (tableRow) {
      return reconnectActions(tableRow.avcId, this.rows, this.preflight.steps, this.record);
    },
    adviceClass (tone) {
      return ADVICE_CLASS[tone] || ADVICE_CLASS.grey;
    },
    openKick () {
      this.includeUncommitted = false;
      this.includeOnline = false;
      this.kickOpen = true;
    },
    kickAll () {
      const ids = this.kickDlg.ids;
      this.kickOpen = false;
      this.run("kick", () => this.cvcKickStart({ ids, kickAll: true }));
    },
    // The not sent and failed services again: a new run over those ids only.
    retryKick () {
      const ids = this.retryIds;
      this.run("kick-retry", () => this.cvcKickStart({ ids, kickAll: false }));
    },
    askKickOne (tableRow) {
      const row = this.rows.find(r => r.avcId === tableRow.avcId);
      if (!row) return;
      this.ask(kickOneText(row, this.target), "Kick", () => this.run(`kick-${row.avcId}`, async () => {
        const result = await this.cvcKickStart({ ids: [row.radiusServiceId], kickAll: false, follow: false });
        this.alert = kickThisAlert(result, row, this.preflight.poiState);
      }));
    },
    downloadGuidance () {
      const csv = toKickGuidanceCsv(this.guidance.items, { cvcName: this.preflight.cvcName, nowUtc: new Date() });
      downloadText(csv.filename, csv.text);
    },
    verifyOne (tableRow) {
      this.run(`verify-${tableRow.avcId}`, () => this.verifyRun([tableRow.avcId]));
    },
    refreshReconnect () {
      this.run("reconnect", async () => {
        this.$emit("reconnect", await this.cvcMigrationVerifyReconnected({ cvcId: this.cvcId, refresh: false }));
      });
    },
    downloadOffline () {
      const csv = toOfflineCsv(this.table, { cvcName: this.preflight.cvcName, nowUtc: new Date() });
      downloadText(csv.filename, csv.text);
    },
    askMarkMigrated () {
      this.ask(markMigratedText(this.reconnect, this.record), "Mark migrated", () =>
        this.run("migrated", () => this.cvcMigrationMarkMigrated({ cvcId: this.cvcId })));
    },
  },
};
</script>
